import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// Services & Projects — Projects & Campaigns Page
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  FolderKanban, Plus, Search, Eye, Edit, Trash2, MoreVertical
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Modal } from '@/shared/components/ui/Modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/Tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { formatINR } from '@/shared/utils/currency';
import { useProjects, useCampaigns, useProjectMutations, useCampaignMutations } from '../hooks/useServicesProjectsApi';
import { useContracts } from '../hooks/useServicesProjectsApi';
import { useParties } from '@/features/accounts/hooks/useParties';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { localToday } from '@/shared/utils/utils';

const PROJECT_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-100 text-amber-800' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const PROJECT_TYPES = [
  { value: 'fixed_price', label: 'Fixed Price' },
  { value: 'time_material', label: 'Time & Material' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'mixed', label: 'Mixed' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const CAMPAIGN_STATUSES = [
  { value: 'planning', label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'paused', label: 'Paused', color: 'bg-amber-100 text-amber-800' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const CAMPAIGN_PLATFORMS = [
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'meta_ads', label: 'Meta Ads (FB/IG)' },
  { value: 'linkedin_ads', label: 'LinkedIn Ads' },
  { value: 'twitter_ads', label: 'Twitter Ads' },
  { value: 'youtube_ads', label: 'YouTube Ads' },
  { value: 'programmatic', label: 'Programmatic' },
  { value: 'email', label: 'Email Marketing' },
  { value: 'seo', label: 'SEO/Content' },
  { value: 'other', label: 'Other' },
];

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

function getStatusConfig(status: string, type: 'project' | 'campaign') {
  const statuses = type === 'project' ? PROJECT_STATUSES : CAMPAIGN_STATUSES;
  return statuses.find(s => s.value === status) || { label: status, color: 'bg-slate-100 text-slate-700' };
}

export function ProjectsCampaignsPage() {
  const [activeTab] = useState<'projects' | 'campaigns'>('projects');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaigns();
  const { data: contracts = [] } = useContracts({ status: 'active' });
  const { data: clients = [] } = useParties();
  const { data: users = [] } = useUserOptions();
  const { createProject, updateProject, deleteProject } = useProjectMutations();
  const { createCampaign, updateCampaign, deleteCampaign } = useCampaignMutations();

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (search && !p.name?.toLowerCase().includes(search.toLowerCase()) && 
          !p.client?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (typeFilter && p.billingMethod !== typeFilter) return false;
      if (clientFilter && p.clientId !== clientFilter) return false;
      return true;
    });
  }, [projects, search, statusFilter, typeFilter, clientFilter]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase()) && 
          !c.clientName?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      if (clientFilter && c.clientId !== clientFilter) return false;
      return true;
    });
  }, [campaigns, search, statusFilter, clientFilter]);

  const resetForm = useCallback((_isProject = true) => {
    setFormData({
      name: '',
      clientId: '',
      contractId: '',
      projectManagerId: '',
      teamIds: [],
      startDate: localToday(),
      endDate: '',
      status: 'active',
      priority: 'medium',
      department: '',
      tags: '',
      budgetMinor: 0,
      revenueMinor: 0,
      costMinor: 0,
      billingMethod: 'fixed_price',
      // Campaign specific
      platform: '',
      objective: '',
      targetAudience: '',
      agencyFeeMinor: 0,
      markupPct: 0,
      commissionPct: 0,
    });
    setEditing(null);
    setCreating(true);
  }, []);

  const handleCreate = (isProject = true) => {
    resetForm(isProject);
  };

  const handleEdit = (item: any) => {
    setEditing(item);
    setFormData({ ...item });
    setCreating(true);
  };

  const handleView = (item: any) => {
    setViewing(item);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) return toast.error('Name is required');
    if (!formData.clientId) return toast.error('Client is required');

    setSaving(true);
    try {
      const selectedClient = clients.find((c: any) => (c._id || c.id) === formData.clientId);
      const clientName = selectedClient?.name || selectedClient?.displayName || selectedClient?.legalName || selectedClient?.contactId?.displayName || selectedClient?.contactId || formData.clientName || formData.client || '';

      const payload = {
        ...formData,
        client: clientName,
        clientName: clientName,
        budgetMinor: Number(formData.budgetMinor) || 0,
        revenueMinor: Number(formData.revenueMinor) || 0,
        costMinor: Number(formData.costMinor) || 0,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        teamIds: Array.isArray(formData.teamIds) ? formData.teamIds : 
          (formData.teamIds ? formData.teamIds.split(',').map((s: string) => s.trim()) : []),
        tags: formData.tags ? formData.tags.split(',').map((s: string) => s.trim()) : [],
      };

      if (activeTab === 'projects') {
        if (editing) {
          await updateProject.mutateAsync({ id: editing._id, payload });
          toast.success('Project updated');
        } else {
          await createProject.mutateAsync(payload);
          toast.success('Project created');
        }
      } else {
        payload.agencyFeeMinor = Number(formData.agencyFeeMinor) || 0;
        payload.markupPct = Number(formData.markupPct) || 0;
        payload.commissionPct = Number(formData.commissionPct) || 0;
        if (editing) {
          await updateCampaign.mutateAsync({ id: editing._id, payload });
          toast.success('Campaign updated');
        } else {
          await createCampaign.mutateAsync(payload);
          toast.success('Campaign created');
        }
      }
      resetForm(activeTab === 'projects');
    } catch (err) {
      toast.error(errorText(err, `Failed to save ${activeTab.slice(0, -1)}`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, isProject: boolean) => {
    if (!window.confirm(`Delete this ${isProject ? 'project' : 'campaign'}?`)) return;
    try {
      if (isProject) await deleteProject.mutateAsync(id);
      else await deleteCampaign.mutateAsync(id);
      toast.success(`${isProject ? 'Project' : 'Campaign'} deleted`);
    } catch (err) {
      toast.error(errorText(err, 'Failed to delete'));
    }
  };

  const isLoading = activeTab === 'projects' ? projectsLoading : campaignsLoading;
  const filteredItems = activeTab === 'projects' ? filteredProjects : filteredCampaigns;
  const statuses = activeTab === 'projects' ? PROJECT_STATUSES : CAMPAIGN_STATUSES;
  const types = activeTab === 'projects' ? PROJECT_TYPES : CAMPAIGN_PLATFORMS;
  const typeLabel = activeTab === 'projects' ? 'Billing Model' : 'Platform';

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={FolderKanban}
        eyebrow="Projects & Services"
        title={activeTab === 'projects' ? 'Projects' : 'Campaigns'}
        description={activeTab === 'projects'
          ? 'Manage projects, track progress, budgets, and profitability'
          : 'Manage marketing campaigns, track spend, and measure ROI'}
        actions={
          <Button onClick={() => handleCreate(activeTab === 'projects')}>
            <Plus className="w-4 h-4" />
            New {activeTab === 'projects' ? 'Project' : 'Campaign'}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <Input
                placeholder="Search by name, client..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                options={[{ value: '', label: 'All Status' }, ...statuses.map(s => ({ value: s.value, label: s.label }))]}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">{typeLabel}</label>
              <Select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                options={[{ value: '', label: `All ${typeLabel}` }, ...types.map(t => ({ value: t.value, label: t.label }))]}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
              <Select
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
                options={[{ value: '', label: 'All Clients' }, ...clients.map((c: any) => ({ value: c._id || c.id, label: c.name || c.displayName || c._id }))]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No {activeTab === 'projects' ? 'projects' : 'campaigns'} found
            </h3>
            <p className="text-gray-500 mb-4">
              {search || statusFilter || typeFilter || clientFilter
                ? 'Try adjusting your filters.'
                : `Create your first ${activeTab === 'projects' ? 'project' : 'campaign'} to get started.`}
            </p>
            {!search && !statusFilter && !typeFilter && !clientFilter && (
              <Button onClick={() => handleCreate(activeTab === 'projects')}>
                <Plus className="w-4 h-4 mr-2" />
                Create {activeTab === 'projects' ? 'Project' : 'Campaign'}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {activeTab === 'projects' ? (
                    <>
                      <TableHead className="w-8"><input type="checkbox" /></TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Contract</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Budget</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="w-8"><input type="checkbox" /></TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Budget / Spend</TableHead>
                      <TableHead>Agency Fee</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item: any) => {
                  const statusConfig = getStatusConfig(item.status, activeTab === 'projects' ? 'project' : 'campaign');
                  return (
                    <TableRow key={item._id}>
                      <TableCell className="w-8">
                        <input type="checkbox" className="rounded border-gray-300" />
                      </TableCell>
                      {activeTab === 'projects' ? (
                        <>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>{item.clientName || item.client?.displayName || item.client?.name || '—'}</TableCell>
                          <TableCell>
                            {item.contractNumber ? (
                              <span className="text-blue-600 hover:underline cursor-pointer">{item.contractNumber}</span>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            {item.projectManager?.displayName || item.projectManager?.name || '—'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.startDate ? new Date(item.startDate).toLocaleDateString() : '—'}
                            {item.endDate && <span className="text-gray-400 mx-1">→</span>}
                            {item.endDate && new Date(item.endDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="font-mono">{formatINR(item.budgetMinor || 0)}</TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.billingMethod?.replace('_', ' ') || '—'}</Badge>
                          </TableCell>
                          <TableCell className="w-48">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleView(item)} title="View">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} title="Edit">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item._id, true)} title="Delete" className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="More">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>{item.clientName || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.platform?.replace('_', ' ').toUpperCase() || '—'}</Badge>
                          </TableCell>
                          <TableCell className="font-mono">
                            {formatINR(item.budgetMinor || 0)} / {formatINR(item.spendMinor || 0)}
                          </TableCell>
                          <TableCell className="font-mono">{formatINR(item.agencyFeeMinor || 0)}</TableCell>
                          <TableCell className="text-sm">
                            {item.startDate ? new Date(item.startDate).toLocaleDateString() : '—'}
                            {item.endDate && <span className="text-gray-400 mx-1">→</span>}
                            {item.endDate && new Date(item.endDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          </TableCell>
                          <TableCell className="w-48">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleView(item)} title="View">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} title="Edit">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item._id, false)} title="Delete" className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="More">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={() => setCreating(false)} title={editing ? `Edit ${activeTab.slice(0, -1)}` : `New ${activeTab.slice(0, -1)}`} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <Tabs defaultValue="details" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="financials">Financials</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name *</label>
                  <Input
                    placeholder={activeTab === 'projects' ? 'Website Redesign Project' : 'Q4 Brand Awareness Campaign'}
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client *</label>
                  <Select
                    value={formData.clientId}
                    onChange={e => setFormData({ ...formData, clientId: e.target.value })}
                    options={clients.map((c: any) => ({ value: c._id || c.id, label: c.name || c.displayName || c.legalName || c.contactId?.displayName || c.contactId || c._id }))}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contract</label>
                  <Select
                    value={formData.contractId}
                    onChange={e => setFormData({ ...formData, contractId: e.target.value })}
                    options={[{ value: '', label: 'No contract linked' }, ...contracts.map(c => ({ value: c._id, label: `${c.contractNumber} — ${c.clientName}` }))]}
                  />
                </div>
                {activeTab === 'projects' ? (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Billing Model</label>
                    <Select
                      value={formData.billingMethod}
                      onChange={e => setFormData({ ...formData, billingMethod: e.target.value })}
                      options={PROJECT_TYPES}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Platform</label>
                    <Select
                      value={formData.platform}
                      onChange={e => setFormData({ ...formData, platform: e.target.value })}
                      options={CAMPAIGN_PLATFORMS}
                    />
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <Select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                    options={statuses}
                  />
                </div>
              </div>

              {activeTab === 'projects' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                    <Select
                      value={formData.priority}
                      onChange={e => setFormData({ ...formData, priority: e.target.value })}
                      options={PRIORITIES}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                    <Input
                      placeholder="Engineering, Marketing, etc."
                      value={formData.department}
                      onChange={e => setFormData({ ...formData, department: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Objective</label>
                    <Select
                      value={formData.objective}
                      onChange={e => setFormData({ ...formData, objective: e.target.value })}
                      options={[
                        { value: 'brand_awareness', label: 'Brand Awareness' },
                        { value: 'lead_generation', label: 'Lead Generation' },
                        { value: 'conversions', label: 'Conversions/Sales' },
                        { value: 'engagement', label: 'Engagement' },
                        { value: 'traffic', label: 'Website Traffic' },
                        { value: 'retention', label: 'Customer Retention' },
                      ]}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Target Audience</label>
                    <Input
                      placeholder="e.g., SMB decision makers, 25-45"
                      value={formData.targetAudience}
                      onChange={e => setFormData({ ...formData, targetAudience: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tags</label>
                <Input
                  placeholder="Comma-separated tags (web, mobile, urgent)"
                  value={formData.tags}
                  onChange={e => setFormData({ ...formData, tags: e.target.value })}
                />
              </div>
            </TabsContent>

            <TabsContent value="financials" className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Budget (₹)</label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="0"
                    value={formData.budgetMinor}
                    onChange={e => setFormData({ ...formData, budgetMinor: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Revenue (₹)</label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="0"
                    value={formData.revenueMinor}
                    onChange={e => setFormData({ ...formData, revenueMinor: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cost (₹)</label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="0"
                    value={formData.costMinor}
                    onChange={e => setFormData({ ...formData, costMinor: e.target.value })}
                  />
                </div>
              </div>

              {activeTab === 'campaigns' && (
                <div className="grid gap-4 sm:grid-cols-3 border-t pt-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Agency Fee (₹)</label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      placeholder="0"
                      value={formData.agencyFeeMinor}
                      onChange={e => setFormData({ ...formData, agencyFeeMinor: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Markup %</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0"
                      value={formData.markupPct}
                      onChange={e => setFormData({ ...formData, markupPct: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Commission %</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0"
                      value={formData.commissionPct}
                      onChange={e => setFormData({ ...formData, commissionPct: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="team" className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Manager</label>
                  <Select
                    value={formData.projectManagerId}
                    onChange={e => setFormData({ ...formData, projectManagerId: e.target.value })}
                    options={[{ value: '', label: 'Unassigned' }, ...users.map(u => ({ value: u.id, label: u.display_name || u.username || u.id }))]}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Team Members</label>
                  <Select
                    value={formData.teamIds?.join(',') || ''}
                    onChange={e => setFormData({ ...formData, teamIds: e.target.value.split(',').filter(Boolean) })}
                    options={users.map(u => ({ value: u.id, label: u.display_name || u.username || u.id }))}
                    multiple
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">Hold Ctrl/Cmd to select multiple team members.</p>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 border-t pt-4 mt-4">
            <Button type="button" variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title={`${activeTab.slice(0, -1)} Details`} size="lg">
        {viewing && (
          <div className="p-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><span className="text-sm text-gray-500">Name</span><p className="font-medium">{viewing.name}</p></div>
              <div><span className="text-sm text-gray-500">Client</span><p>{viewing.clientName || viewing.client?.displayName || '—'}</p></div>
              <div><span className="text-sm text-gray-500">Contract</span><p>{viewing.contractNumber || '—'}</p></div>
              <div><span className="text-sm text-gray-500">Status</span><p><Badge className={getStatusConfig(viewing.status, activeTab === 'projects' ? 'project' : 'campaign').color}>{getStatusConfig(viewing.status, activeTab === 'projects' ? 'project' : 'campaign').label}</Badge></p></div>
              <div><span className="text-sm text-gray-500">Start Date</span><p>{viewing.startDate ? new Date(viewing.startDate).toLocaleDateString() : '—'}</p></div>
              <div><span className="text-sm text-gray-500">End Date</span><p>{viewing.endDate ? new Date(viewing.endDate).toLocaleDateString() : '—'}</p></div>
              <div><span className="text-sm text-gray-500">Budget</span><p className="font-mono">{formatINR(viewing.budgetMinor || 0)}</p></div>
              <div><span className="text-sm text-gray-500">Revenue</span><p className="font-mono text-emerald-600">{formatINR(viewing.revenueMinor || 0)}</p></div>
              <div><span className="text-sm text-gray-500">Cost</span><p className="font-mono text-red-600">{formatINR(viewing.costMinor || 0)}</p></div>
              <div><span className="text-sm text-gray-500">Margin</span><p className="font-bold">
                {viewing.budgetMinor ? (((viewing.revenueMinor - viewing.costMinor) / viewing.budgetMinor) * 100).toFixed(1) + '%' : '—'}
              </p></div>
              {activeTab === 'projects' && (
                <>
                  <div><span className="text-sm text-gray-500">Billing Model</span><p>{viewing.billingMethod?.replace('_', ' ') || '—'}</p></div>
                  <div><span className="text-sm text-gray-500">Priority</span><p><Badge variant="secondary">{viewing.priority}</Badge></p></div>
                  <div><span className="text-sm text-gray-500">Manager</span><p>{viewing.projectManager?.displayName || '—'}</p></div>
                </>
              )}
              {activeTab === 'campaigns' && (
                <>
                  <div><span className="text-sm text-gray-500">Platform</span><p><Badge variant="secondary">{viewing.platform?.replace('_', ' ').toUpperCase() || '—'}</Badge></p></div>
                  <div><span className="text-sm text-gray-500">Objective</span><p>{viewing.objective?.replace('_', ' ') || '—'}</p></div>
                  <div><span className="text-sm text-gray-500">Agency Fee</span><p className="font-mono">{formatINR(viewing.agencyFeeMinor || 0)}</p></div>
                  <div><span className="text-sm text-gray-500">Spend</span><p className="font-mono">{formatINR(viewing.spendMinor || 0)}</p></div>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}